import React, { useState } from 'react';
import { motion } from 'framer-motion';
import projects from '../../data/projects';
import SectionReveal from './SectionReveal';

const RARITY = {
	legendary: {
		label: 'Legendary',
		text: 'text-legendary',
		glow: 'shadow-[0_0_25px_rgba(245,166,35,0.35)]',
		tag: 'text-legendary border-legendary/60',
	},
	epic: {
		label: 'Epic',
		text: 'text-epic',
		glow: 'shadow-[0_0_25px_rgba(168,85,247,0.35)]',
		tag: 'text-epic border-epic/60',
	},
	rare: {
		label: 'Rare',
		text: 'text-rare',
		glow: 'shadow-[0_0_20px_rgba(91,124,250,0.3)]',
		tag: 'text-rare border-rare/60',
	},
};

const QuestCard = ({ project, index }) => {
	const [unlocked, setUnlocked] = useState(false);
	const rarity = RARITY[project.rarity] || RARITY.rare;

	return (
		<motion.div
			initial={{ opacity: 0, y: 30, rotate: -3, scale: 0.94 }}
			whileInView={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
			viewport={{ once: true, amount: 0.3 }}
			onViewportEnter={() => {
				setUnlocked(true);
				setTimeout(() => setUnlocked(false), 1800);
			}}
			whileHover={{ y: -6, rotate: -0.5 }}
			transition={{ type: 'spring', stiffness: 120, damping: 14, delay: index * 0.08 }}
			className={`group relative bg-panel rounded-lg border-2 border-black ${rarity.glow} hover:border-blorange transition-colors p-6 flex flex-col`}
		>
			{/* Halftone comic texture */}
			<div
				className="absolute inset-0 rounded-lg opacity-[0.06] pointer-events-none"
				style={{
					backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
					backgroundSize: '6px 6px',
				}}
				aria-hidden="true"
			/>

			{unlocked && (
				<motion.div
					initial={{ opacity: 0, y: -8, scale: 0.9 }}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					exit={{ opacity: 0 }}
					className="absolute -top-3 left-1/2 -translate-x-1/2 font-mono text-[10px] px-2 py-1 rounded bg-ink border-2 border-black text-blorange whitespace-nowrap z-10"
				>
					🏆 Achievement Unlocked
				</motion.div>
			)}
			<div className={`relative font-mono text-[10px] uppercase tracking-widest mb-2 ${rarity.text}`}>
				★ {rarity.label} Quest
			</div>
			<h3 className="relative font-display text-lg font-semibold mb-2 text-gray-100 group-hover:text-blorange transition-colors">
				{project.link ? (
					<a
						href={project.link}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:underline"
					>
						{project.title}
					</a>
				) : (
					project.title
				)}
			</h3>
			<p className="relative text-sm text-gray-400 mb-4">{project.description}</p>
			<div className="relative flex flex-wrap gap-2 mt-auto">
				{project.tags.map(tag => (
					<span
						key={tag}
						className={`px-2.5 py-1 rounded border-2 bg-ink font-mono text-[10px] ${rarity.tag}`}
					>
						{tag}
					</span>
				))}
			</div>
		</motion.div>
	);
};

const ProjectsSectionV2 = () => (
	<section id="projects" className="py-24 bg-ink/90">
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<SectionReveal className="text-center mb-16">
				<div className="font-mono text-xs uppercase tracking-[0.3em] text-rpgblue mb-2">
					Adventure Log
				</div>
				<h2 className="font-display text-3xl font-semibold mb-4 text-gold">Quest Log</h2>
				<div className="w-20 h-1 bg-blorange mx-auto rounded"></div>
			</SectionReveal>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
				{projects.map((project, idx) => (
					<QuestCard key={idx} project={project} index={idx} />
				))}
			</div>
		</div>
	</section>
);

export default ProjectsSectionV2;
